import { useState, useEffect, useContext } from "react";
import { differenceBy } from "lodash";
// @ts-nocheck
import { ContentfulClient, Contentfully, QueryResult } from "contentfully";
import { isAvailable, logicStateReducer } from "../logic";
import { LocaleContext, Locales } from "../locale";
import { Audience, AudienceContext, Onboarding } from "../furniture";
import Constants from "expo-constants";
import { MyPlanConstants } from "myplan";

const {
    contentfulApiAccessToken,
    contentfulApiSpaceId,
    contentfulEnvironmentId,
} = Constants.expoConfig?.extra as MyPlanConstants;

export enum QuestionType {
    YesOrNo = "YES_OR_NO",
    TrueOrFalse = "TRUE_OR_FALSE",
    FactOrMyth = "FACT_OR_MYTH",
    GenderIdentity = "GENDER_IDENTITY",
    SingleSelect = "SINGLE_SELECT",
    MultipleSelect = "MULTIPLE_SELECT",
    Interstitial = "INTERSTITIAL",
    Priority = "PRIORITY",
}

export const defaultOptionsBodyValuesMap = {
    [QuestionType.YesOrNo]: ["Yes", "No"],
    [QuestionType.TrueOrFalse]: ["True", "False"],
    [QuestionType.FactOrMyth]: ["Fact", "Myth"],
    [QuestionType.GenderIdentity]: [
        "Female",
        "Male",
        "Bi- or Tri-gendered",
        "Nongendered",
        "Genderfluid",
        "Third gendered",
    ],
};

const client = new ContentfulClient({
    accessToken: contentfulApiAccessToken,
    spaceId: contentfulApiSpaceId,
    environmentId: contentfulEnvironmentId,
    fetch: fetch,
});

const contentfully = new Contentfully(client);

export const useContentful = (
    query: Promise<QueryResult> | null,
    formatFn?: (result: QueryResult) => any,
) => {
    const [data, setData] = useState<QueryResult | any>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState();
    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        if (query) {
            query
                .then(_data => {
                    const result = formatFn ? formatFn(_data) : _data;
                    setData(result);
                    setIsLoading(false);
                })
                .catch(_error => {
                    setError(_error);
                    setIsLoading(false);
                });
        }
    }, [query, refreshToken]);

    const refresh = () => {
        setIsLoading(true);
        setRefreshToken(refreshToken + 1);
    };

    const namespace = {
        data,
        isLoading,
        error,
        refresh,
    };
    const list = [data, isLoading, error, refresh];
    const hook: [typeof namespace, typeof list] = [namespace, list];

    return hook;
};

export const useFurniture = (
    overrideLocale: Locales | null = null,
    overrideAudience: Audience | null = null,
) => {
    const [query, setQuery] = useState<Promise<QueryResult> | null>(null);
    const { locale } = useContext(LocaleContext);
    const { audience } = useContext(AudienceContext);
    let loc = overrideLocale || locale;
    let aud = overrideAudience || audience;

    useEffect(() => {
        const _query = contentfully.getModels({
            content_type: "furniture",
            locale: loc,
        });

        setQuery(_query);
    }, [loc, aud]);

    // get specified furniture
    const hook = useContentful(query, result => {
        let found = result.items.find(i => i.identifier === aud);
        return found || result.items[0];
    });

    return hook;
};

export const useOnboarding = (
    overrideLocale: Locales | null = null,
    onboarding = Onboarding.Default,
) => {
    const [query, setQuery] = useState<Promise<QueryResult> | null>(null);
    const { locale } = useContext(LocaleContext);
    let loc = overrideLocale || locale;

    useEffect(() => {
        const _query = contentfully.getModels({
            content_type: "onboarding",
            locale: loc,
            limit: 3,
        });

        setQuery(_query);

        // _query.then(result => console.log(result));
    }, [loc]);

    // get default onboarding and return as data
    const hook = useContentful(query, result => {
        let found = result.items.find(i => i.identifier === onboarding);
        return found || result.items[0];
    });

    return hook;
};

export const useResourceCategories = (overrideLocale: Locales = null) => {
    const [query, setQuery] = useState(null);
    const { locale } = useContext(LocaleContext);
    let loc = overrideLocale || locale;

    useEffect(() => {
        const _query = contentfully.getModels({
            content_type: "resourceCategory",
            locale: loc,
        });

        setQuery(_query);
    }, [loc]);

    // get resourceCategories for use in Resources Near Me
    const hook = useContentful(query, result => result.items);

    return hook;
};

export const useQuotes = () => {
    const [query, setQuery] = useState<Promise<QueryResult> | null>(null);
    const { locale } = useContext(LocaleContext);

    let queryParams = {};
    if (locale === Locales.ES) {
        queryParams = {
            "metadata.tags[locale_spanish]": true,
            "metadata.tags.sys.id[in]": "locale_spanish",
        };
    }

    useEffect(() => {
        const _query = contentfully.getModels({
            content_type: "quote",
            locale,
            ...queryParams,
        });

        setQuery(_query);
    }, [locale]);

    const hook = useContentful(query, result => result.items);

    return hook;
};

export const useQuiz = (quizId: string) => {
    const [query, setQuery] = useState<Promise<QueryResult> | null>(null);
    const { locale } = useContext(LocaleContext);

    useEffect(() => {
        const _query = contentfully.getModels({
            content_type: "quiz",
            "sys.id": quizId,
            locale,
        });

        setQuery(_query);
    }, []);

    const hook = useContentful(query, result => result.items[0]);

    return hook;
};

export const useDefaultOptions = () => {
    const [query, setQuery] = useState<Promise<QueryResult> | null>(null);
    const { locale } = useContext(LocaleContext);

    useEffect(() => {
        const _query = contentfully.getModels({
            content_type: "defaultOption",
            locale,
        });

        setQuery(_query);
    }, []);

    const hook = useContentful(query, result => result.items);

    return hook;
};

export const getDefaultOptionsForType = (
    questionType: QuestionType,
    furniture,
) => {
    let result = [];

    switch (questionType) {
        case QuestionType.GenderIdentity:
            result = furniture.defaultOptionIndexGenderIdentity;
            break;

        case QuestionType.YesOrNo:
            result = furniture.defaultOptionIndexYesOrNo;
            break;

        case QuestionType.TrueOrFalse:
            result = furniture.defaultOptionIndexTrueOrFalse;
            break;

        case QuestionType.FactOrMyth:
            result = furniture.defaultOptionIndexFactOrMyth;
            break;
    }

    return result;
};

export const getQuizOrder = (quizStates, logicStates, quizIndex) => {
    const external = logicStates.reduce(
        (acc, logicState) => {
            const result = logicState.stream.reduce(logicStateReducer, acc);

            return result;
        },
        {
            local: {},
            global: {},
        },
    );

    const logicState = {
        external,
        internal: {
            local: {},
            global: {},
        },
    };

    const available = quizIndex.filter(quiz => {
        return isAvailable(quiz, logicState);
    });
    const completed = available.filter(quiz =>
        quizStates.find(quizState => quizState.quizId === quiz._id),
    );
    const queue = available.filter(
        quiz => !quizStates.find(quizState => quizState.quizId === quiz._id),
    );
    const next = queue.splice(0, 1)[0];

    return {
        next,
        queue,
        completed,
        available,
    };
};

export const getStrategyOrder = (logicStates, strategyIndex) => {
    const external = logicStates.reduce(
        (acc, logicState) => {
            const result = logicState.stream.reduce(logicStateReducer, acc);

            return result;
        },
        {
            local: {},
            global: {},
        },
    );

    const logicState = {
        external,
        internal: {
            local: {},
            global: {},
        },
    };

    const all = strategyIndex;
    const available = strategyIndex.filter(strategy =>
        isAvailable(strategy, logicState),
    );
    const unavailable = differenceBy(
        all,
        available,
        (strategy: any) => strategy._id,
    );

    return {
        all,
        available,
        unavailable,
    };
};

export const getResourceOrder = (logicStates, resourceIndex) => {
    const external = logicStates.reduce(
        (acc, logicState) => {
            const result = logicState.stream.reduce(logicStateReducer, acc);

            return result;
        },
        {
            local: {},
            global: {},
        },
    );

    const logicState = {
        external,
        internal: {
            local: {},
            global: {},
        },
    };

    const all = resourceIndex;
    const available = resourceIndex.filter(_resource =>
        isAvailable(_resource, logicState),
    );
    const unavailable = differenceBy(
        all,
        available,
        (resource: any) => resource._id,
    );

    return {
        all,
        available,
        unavailable,
    };
};

export const getQuestionCount = quiz => {
    const validQuestions = quiz.questions.filter(
        question => question.type !== QuestionType.Interstitial,
    );

    return validQuestions.length;
};

export const useResourceCategoryIndex = () => {
    const query = contentfully.getModels({
        content_type: "furniture",
    });
    const hook = useContentful(query, result => ({
        resources: result.items[0].resourceCategoryIndex,
        title: result.items[0].myResourcesTitle,
    }));

    return hook;
};

export default {
    QuestionType,
    defaultOptionsBodyValuesMap,
    useFurniture,
    useResourceCategories,
    useQuotes,
    useQuiz,
    useDefaultOptions,
    getDefaultOptionsForType,
    getQuizOrder,
    getStrategyOrder,
    getResourceOrder,
    getQuestionCount,
    useResourceCategoryIndex,
};
